import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Sunday

export type WorkingCalendarRecord = {
  id: string;
  name: string;
  weeklyOffs: Weekday[];
  holidays: CalendarHolidayRecord[];
  createdAt: string;
  updatedAt: string;
};

export type CalendarHolidayRecord = {
  id: string;
  title: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD or ""
  notes: string;
  createdAt: string;
  updatedAt: string;
};

type WorkingCalendarApi = {
  id?: number | string;
  name?: string | null;
  weekly_offs?: unknown;
  holidays?: CalendarHolidayApi[] | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type CalendarHolidayApi = {
  id?: number | string;
  working_calendar_id?: number | string;
  title?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type GetCalendarResponse = { data?: WorkingCalendarApi };
type UpsertCalendarResponse = { message?: string; data?: WorkingCalendarApi };
type HolidayListResponse = { data?: CalendarHolidayApi[]; meta?: unknown; links?: unknown };
type UpsertHolidayResponse = { message?: string; data?: CalendarHolidayApi };
type DeleteHolidayResponse = { message?: string };

const LS_KEY = "inventory.workingCalendar.v1";
const HOLIDAYS_LS_KEY = "inventory.workingCalendar.holidays.v1";

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function normalizeWeeklyOffs(raw: unknown): Weekday[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  for (const x of raw) {
    const n = typeof x === "number" ? x : Number(x);
    if (!Number.isFinite(n)) continue;
    const i = Math.trunc(n);
    if (i >= 0 && i <= 6) out.push(i);
  }
  return Array.from(new Set(out)).sort((a, b) => a - b) as Weekday[];
}

function mapHolidayApi(row: CalendarHolidayApi): CalendarHolidayRecord {
  return {
    id: str(row.id),
    title: str(row.title),
    startDate: str(row.start_date).slice(0, 10),
    endDate: str(row.end_date).slice(0, 10),
    notes: str(row.notes),
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at),
  };
}

function mapCalendarApi(cal: WorkingCalendarApi): WorkingCalendarRecord {
  return {
    id: str(cal.id),
    name: str(cal.name) || "Global",
    weeklyOffs: normalizeWeeklyOffs(cal.weekly_offs),
    holidays: Array.isArray(cal.holidays) ? cal.holidays.map(mapHolidayApi) : listCalendarHolidays(),
    createdAt: str(cal.created_at),
    updatedAt: str(cal.updated_at),
  };
}

function loadCalendar(): WorkingCalendarRecord | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as WorkingCalendarRecord;
  } catch {
    return null;
  }
}

function saveCalendar(cal: WorkingCalendarRecord) {
  localStorage.setItem(LS_KEY, JSON.stringify(cal));
}

function loadHolidays(): CalendarHolidayRecord[] {
  try {
    const raw = localStorage.getItem(HOLIDAYS_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CalendarHolidayRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHolidays(items: CalendarHolidayRecord[]) {
  localStorage.setItem(HOLIDAYS_LS_KEY, JSON.stringify(items));
}

export function getWorkingCalendarCached(): WorkingCalendarRecord | null {
  const cal = loadCalendar();
  if (!cal) return null;
  return { ...cal, holidays: listCalendarHolidays() };
}

export function listCalendarHolidays(): CalendarHolidayRecord[] {
  return loadHolidays().slice().sort((a, b) => (b.startDate || "").localeCompare(a.startDate || "") || b.id.localeCompare(a.id));
}

export function workingCalendarApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchWorkingCalendar(): Promise<WorkingCalendarRecord> {
  try {
    const response = await api.get<GetCalendarResponse>("/api/v1/working-calendar");
    const cal = mapCalendarApi(response.data?.data ?? {});
    saveCalendar(cal);
    if (cal.holidays) saveHolidays(cal.holidays);
    return getWorkingCalendarCached() ?? cal;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    throw error;
  }
}

export async function updateWorkingCalendar(payload: { name?: string; weeklyOffs?: Weekday[] }): Promise<{ record: WorkingCalendarRecord; message: string }> {
  const response = await api.put<UpsertCalendarResponse>("/api/v1/working-calendar", {
    ...(payload.name !== undefined ? { name: payload.name } : {}),
    ...(payload.weeklyOffs !== undefined ? { weekly_offs: payload.weeklyOffs } : {}),
  });
  const cal = mapCalendarApi(response.data?.data ?? {});
  saveCalendar(cal);
  if (Array.isArray(cal.holidays)) saveHolidays(cal.holidays);
  return { record: getWorkingCalendarCached() ?? cal, message: response.data?.message || "Working calendar updated." };
}

export async function syncHolidaysFromApi(): Promise<CalendarHolidayRecord[]> {
  try {
    const collected: CalendarHolidayApi[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get<HolidayListResponse>("/api/v1/working-calendar/holidays", { params: { page, per_page: 50 } });
      const body = response.data as unknown as { data?: CalendarHolidayApi[]; meta?: { last_page?: number } };
      if (Array.isArray(body.data)) collected.push(...body.data);
      const lp = body.meta?.last_page;
      if (typeof lp === "number" && lp >= 1) lastPage = lp;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = collected.map(mapHolidayApi).filter((r) => Boolean(r.id));
    saveHolidays(records);
    return listCalendarHolidays();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    throw error;
  }
}

export async function createHoliday(payload: { title: string; startDate: string; endDate?: string; notes?: string }): Promise<{ record: CalendarHolidayRecord; message: string }> {
  const response = await api.post<UpsertHolidayResponse>("/api/v1/working-calendar/holidays", {
    title: payload.title,
    start_date: payload.startDate,
    end_date: payload.endDate?.trim() ? payload.endDate.trim() : null,
    notes: payload.notes?.trim() ? payload.notes.trim() : null,
  });
  const rec = mapHolidayApi(response.data?.data ?? {});
  const items = loadHolidays();
  items.unshift(rec);
  saveHolidays(items);
  return { record: rec, message: response.data?.message || "Holiday created." };
}

export async function updateHoliday(id: string, payload: { title: string; startDate: string; endDate?: string; notes?: string }): Promise<{ record: CalendarHolidayRecord; message: string } | null> {
  const items = loadHolidays();
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return null;
  const response = await api.put<UpsertHolidayResponse>(`/api/v1/working-calendar/holidays/${id}`, {
    title: payload.title,
    start_date: payload.startDate,
    end_date: payload.endDate?.trim() ? payload.endDate.trim() : null,
    notes: payload.notes?.trim() ? payload.notes.trim() : null,
  });
  const rec = mapHolidayApi(response.data?.data ?? {});
  items[idx] = rec;
  saveHolidays(items);
  return { record: rec, message: response.data?.message || "Holiday updated." };
}

export async function deleteHoliday(id: string): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<DeleteHolidayResponse>(`/api/v1/working-calendar/holidays/${id}`);
  const items = loadHolidays();
  const next = items.filter((x) => x.id !== id);
  const removed = next.length !== items.length;
  if (removed) saveHolidays(next);
  return { success: removed, message: response.data?.message || "Holiday deleted." };
}

